import React, { useState } from 'react';
import { db, storage } from '../firebase';
import { doc, setDoc, Timestamp, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './AdminPage.css';

const AdminPage = () => {
    const [activeTab, setActiveTab] = useState('bulk');
    const [csvData, setCsvData] = useState('');
    const [modelLineCsvData, setModelLineCsvData] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [uploadedImageUrl, setUploadedImageUrl] = useState('');
    const [imageUploadLoading, setImageUploadLoading] = useState(false);
    const [uploadedLogoUrl, setUploadedLogoUrl] = useState('');
    const [logoUploadLoading, setLogoUploadLoading] = useState(false);
    const [advertiserCsvData, setAdvertiserCsvData] = useState('');

    const handleBulkImport = async () => {
        setLoading(true);
        setMessage('');

        try {
            const normalizedData = csvData.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
            const rows = normalizedData
                .split('\n')
                .slice(1)
                .filter(row => row.trim() !== '');
            let successCount = 0;

            for (const row of rows) {
                const columns = row.replace(/\r$/, '').split('\t');
                while (columns.length < 25) {
                    columns.push('');
                }
                if (!columns[0]?.trim() || !columns[1]?.trim() || !columns[7]?.trim()) continue;

                const auctionDate = new Date(columns[1]);
                const dateStr = auctionDate.toISOString().split('T')[0];
                const vin = columns[7];
                const docId = `${dateStr}-${vin}`;
                const docRef = doc(db, 'auctions', docId);

                const auctionData = {
                    auctionLink: columns[0],
                    auctionDate: Timestamp.fromDate(auctionDate),
                    make: columns[2],
                    year: Number(columns[3]),
                    title: columns[4],
                    modelId: columns[5],
                    miles: columns[6],
                    vin: columns[7],
                    transmission: columns[8],
                    exteriorColor: columns[9],
                    colorFamily: columns[10],
                    interiorColor: columns[11],
                    intColorFamily: columns[12],
                    owners: columns[13],
                    modifier1: columns[14],
                    modifier2: columns[15],
                    modifier3: columns[16],
                    condition: columns[17],
                    accident: columns[18],
                    region: columns[19],
                    auctionHouse: columns[20],
                    auctionPlatform: columns[21],
                    imageLink: columns[22],
                    finalBid: columns[23]?.trim() ? Number(columns[23].replace(/[^0-9]/g, '')) : null,
                    sold: columns[24]?.trim() ? columns[24].trim().toLowerCase() === 'true' : null
                };

                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    // Document exists, update fields (add, modify, or delete)
                    const existingData = docSnap.data();
                    const updateData = {};
                    // Add/update fields from auctionData
                    for (const key in auctionData) {
                        if (auctionData[key] !== undefined && auctionData[key] !== null) {
                            updateData[key] = auctionData[key];
                        }
                    }
                    // Remove fields not present in auctionData
                    for (const key in existingData) {
                        if (!(key in auctionData)) {
                            updateData[key] = deleteField();
                        }
                    }
                    await updateDoc(docRef, updateData);
                } else {
                    // Document does not exist, create new
                    await setDoc(docRef, auctionData);
                }
                successCount++;
            }

            setMessage(`Successfully imported ${successCount} auctions!`);
            setCsvData('');
        } catch (error) {
            setMessage('Error importing data: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleModelLineImport = async () => {
        setLoading(true);
        setMessage('');

        try {
            const normalizedData = modelLineCsvData.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
            const rows = normalizedData
                .split('\n')
                .slice(1)
                .filter(row => row.trim() !== '');
            let successCount = 0;

            for (const row of rows) {
                const columns = row.replace(/\r$/, '').split('\t');
                while (columns.length < 9) {
                    columns.push('');
                }
                if (!columns[4]?.trim()) continue;

                const modelLineData = {
                    make: columns[0],
                    modelLine: columns[1],
                    model: columns[2],
                    year: columns[3],
                    modelId: columns[4],
                    description: columns[5],
                    imageLink: columns[6],
                    estimator: columns[7],
                    marketModel: columns[8]
                };

                // Use setDoc instead of addDoc to set the document ID to the modelId
                const modelId = modelLineData.modelId.trim();
                if (modelId) {
                    await setDoc(doc(db, 'modelLines', modelId), modelLineData);
                    successCount++;
                }
            }

            setMessage(`Successfully imported ${successCount} model lines!`);
            setModelLineCsvData('');
        } catch (error) {
            setMessage('Error importing model lines: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImageUploadLoading(true);
        setMessage('');

        try {
            // Create a unique filename using timestamp
            const timestamp = new Date().getTime();
            const fileName = `${timestamp}-${file.name}`;
            const storageRef = ref(storage, `model-images/${fileName}`);
            
            // Upload the image
            await uploadBytes(storageRef, file);
            
            // Get the download URL
            const url = await getDownloadURL(storageRef);
            setUploadedImageUrl(url);
            setMessage('Image uploaded successfully!');
        } catch (error) {
            setMessage('Error uploading image: ' + error.message);
        } finally {
            setImageUploadLoading(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLogoUploadLoading(true);
        setMessage('');

        try {
            // Create a unique filename using timestamp
            const timestamp = new Date().getTime();
            const fileName = `${timestamp}-${file.name}`;
            const storageRef = ref(storage, `company-logo/${fileName}`);
            
            // Upload the logo
            await uploadBytes(storageRef, file);
            
            // Get the download URL
            const url = await getDownloadURL(storageRef);
            setUploadedLogoUrl(url);
            setMessage('Logo uploaded successfully!');
        } catch (error) {
            setMessage('Error uploading logo: ' + error.message);
        } finally {
            setLogoUploadLoading(false);
        }
    };

    const handleAdvertiserImport = async () => {
        setLoading(true);
        setMessage('');

        try {
            const rows = advertiserCsvData.trim().split('\n').slice(1); // Skip header row
            let successCount = 0;

            for (const row of rows) {
                const columns = row.split('\t');
                if (columns.length < 4) continue;

                const companyName = columns[0].trim();
                if (!companyName) continue;

                const advertiserData = {
                    companyName: companyName,
                    logo: columns[1].trim(),
                    copy: columns[2].trim(),
                    link: columns[3].trim()
                };

                // Use company name as document ID
                await setDoc(doc(db, 'advertisers', companyName), advertiserData);
                successCount++;
            }

            setMessage(`Successfully imported ${successCount} advertisers!`);
            setAdvertiserCsvData('');
        } catch (error) {
            setMessage('Error importing advertisers: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-container">
            <h1>Auction Data Management</h1>
            
            <div className="admin-tabs">
                <button 
                    className={`tab-button ${activeTab === 'bulk' ? 'active' : ''}`}
                    onClick={() => setActiveTab('bulk')}
                >
                    Bulk Import
                </button>
                <button 
                    className={`tab-button ${activeTab === 'modelLines' ? 'active' : ''}`}
                    onClick={() => setActiveTab('modelLines')}
                >
                    Model Lines
                </button>
                <button 
                    className={`tab-button ${activeTab === 'images' ? 'active' : ''}`}
                    onClick={() => setActiveTab('images')}
                >
                    Upload Images
                </button>
                <button 
                    className={`tab-button ${activeTab === 'logo' ? 'active' : ''}`}
                    onClick={() => setActiveTab('logo')}
                >
                    Upload Logo
                </button>
                <button 
                    className={`tab-button ${activeTab === 'advertisers' ? 'active' : ''}`}
                    onClick={() => setActiveTab('advertisers')}
                >
                    Advertisers
                </button>
            </div>

            {message && (
                <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
                    {message}
                </div>
            )}

            {activeTab === 'bulk' ? (
                <div className="form-container">
                    <p>Paste your tab-separated data from Google Sheets below (including headers):</p>
                    <textarea
                        className="csv-input"
                        value={csvData}
                        onChange={(e) => setCsvData(e.target.value)}
                        placeholder="Paste data here..."
                    />
                    <button 
                        onClick={handleBulkImport} 
                        className="submit-button"
                        disabled={loading || !csvData.trim()}
                    >
                        {loading ? 'Importing...' : 'Import Data'}
                    </button>
                </div>
            ) : activeTab === 'modelLines' ? (
                <div className="form-container">
                    <p>Expected columns: make, modelLine, model, year, modelId, description, imageLink, estimator, marketModel</p>
                    <textarea
                        className="csv-input"
                        value={modelLineCsvData}
                        onChange={(e) => setModelLineCsvData(e.target.value)}
                        placeholder="Paste data here..."
                    />
                    <button 
                        onClick={handleModelLineImport} 
                        className="submit-button"
                        disabled={loading || !modelLineCsvData.trim()}
                    >
                        {loading ? 'Importing...' : 'Import Model Lines'}
                    </button>
                </div>
            ) : activeTab === 'images' ? (
                <div className="form-container">
                    <h2>Upload Image</h2>
                    <div className="form-group">
                        <label>Select Image</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            disabled={imageUploadLoading}
                        />
                    </div>
                    {imageUploadLoading && <p>Uploading...</p>}
                    {uploadedImageUrl && (
                        <div className="uploaded-image-container">
                            <h3>Image URL:</h3>
                            <div className="url-container">
                                <input
                                    type="text"
                                    value={uploadedImageUrl}
                                    readOnly
                                    onClick={(e) => e.target.select()}
                                />
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(uploadedImageUrl);
                                        setMessage('URL copied to clipboard!');
                                    }}
                                >
                                    Copy URL
                                </button>
                            </div>
                            <div className="image-preview">
                                <img src={uploadedImageUrl} alt="Uploaded preview" />
                            </div>
                        </div>
                    )}
                </div>
            ) : activeTab === 'logo' ? (
                <div className="form-container">
                    <h2>Upload Company Logo</h2>
                    <div className="form-group">
                        <label>Select Logo</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            disabled={logoUploadLoading}
                        />
                    </div>
                    {logoUploadLoading && <p>Uploading...</p>}
                    {uploadedLogoUrl && (
                        <div className="uploaded-image-container">
                            <h3>Logo URL:</h3>
                            <div className="url-container">
                                <input
                                    type="text"
                                    value={uploadedLogoUrl}
                                    readOnly
                                    onClick={(e) => e.target.select()}
                                />
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(uploadedLogoUrl);
                                        setMessage('URL copied to clipboard!');
                                    }}
                                >
                                    Copy URL
                                </button>
                            </div>
                            <div className="image-preview">
                                <img src={uploadedLogoUrl} alt="Logo preview" />
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="form-container">
                    <h2>Import Advertisers</h2>
                    <p>Expected columns: company name, logo, copy, link</p>
                    <textarea
                        className="csv-input"
                        value={advertiserCsvData}
                        onChange={(e) => setAdvertiserCsvData(e.target.value)}
                        placeholder="Paste data here..."
                    />
                    <button 
                        onClick={handleAdvertiserImport} 
                        className="submit-button"
                        disabled={loading || !advertiserCsvData.trim()}
                    >
                        {loading ? 'Importing...' : 'Import Advertisers'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default AdminPage;
